import { combineLatest, Observable, of } from 'rxjs'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { catchError, filter, map, switchMap } from 'rxjs/operators'
import { LOGGER_SERVICE } from '../../logger/logger.config'
import { LoggerService } from '../../logger/logger.interface'
import { EventTickerPartial, EventTickerViewModel } from '@flocasts/experience-service-types'
import { EXPERIENCE_API_EVENT_TICKER_PARTIAL_PATH, EXPERIENCE_API_VERSION } from '../../app.config'
import { isEventTickerViewModel } from 'src/app/shared/functions/type-guards'
import { CacheDataService } from 'src/app/singleton-services/cache-data.service'
import { VerticalService } from 'src/app/singleton-services/vertical.service'
import { SiteIds } from 'src/app/shared/models/site.model'
import {
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION
} from '../../shared/experimentation/experiment.model'
import { ExperimentationService } from '../../shared/experimentation/experimentation.service'

const DEFAULT_EVENT_LIMIT = 15

/**
 * Service to fetch data needed to populate the event-ticker from experience-service
 */
@Injectable()
export class EventTickerDataService {
  constructor(
    private readonly cacheDataService: CacheDataService,
    private readonly verticalService: VerticalService,
    private readonly experimentationService: ExperimentationService,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService
  ) {}

  public readonly isNextGenEventHub$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION,
    false
  )
  public readonly isNextGenEventHubShort$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
    false
  )

  public readonly siteId$ = this.verticalService.siteId$
  /**
   * Fetch data from Experience Service's event-ticker partial endpoint.
   */
  public getEventTicker(limit: number = DEFAULT_EVENT_LIMIT): Observable<EventTickerViewModel> {
    return combineLatest([this.isNextGenEventHub$, this.isNextGenEventHubShort$, this.siteId$]).pipe(
      switchMap(([isNextGenEventHub, isNextGenEventHubShort, siteId]) => {
        const params = new HttpParams({
          fromObject: {
            version: EXPERIENCE_API_VERSION,
            limit: limit.toString(),
            site_id: siteId || SiteIds.FLODOGS,
            isNextGenEventHub: (isNextGenEventHub || isNextGenEventHubShort) ?? 'false'
          }
        })
        const endpoint = EXPERIENCE_API_EVENT_TICKER_PARTIAL_PATH
        return this.cacheDataService.wrapper<EventTickerPartial>(endpoint, params, true).pipe(
          map(res => res?.data),
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of({} as EventTickerViewModel)
          }),
          filter(isEventTickerViewModel)
        )
      })
    )
  }
}
